import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucidePlay, lucideX } from '@ng-icons/lucide';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import type { HintCaret } from '@lite-workspace/shared/helm-ui/hint';
import type { CoachAction, OnboardingVideo } from '../catalog.types';
import { COACH_BODY, COACH_FOOTER, COACH_PLATE, COACH_TITLE, type CoachCaretAlign, coachCaret } from './coach-card.classes';
import { OnboardingMediaService } from './onboarding-media.service';

/** Общая обложка: у урока без своей обложки и у двери в библиотеку. Кадр ролика сюда не идёт. */
const COACH_COVER = '/assets/illustrations/onboarding-video-placeholder.jpg';

/**
 * Диск проигрывания поверх обложки: стекло с белым треугольником, на наведении — акцент. Хост задаёт
 * `--coach-t-props` — список перекрашиваемых свойств перехода.
 */
export const PLAY_DISC = `absolute top-1/2 left-1/2 flex size-12 -translate-x-1/2 -translate-y-1/2 items-center
  justify-center rounded-full border border-on-media/26 bg-[var(--scrim-ctrl-2)] backdrop-blur-[var(--blur-panel)]
  transition-[var(--coach-t-props)] duration-[var(--t-fast)]
  group-hover:scale-108 group-hover:border-transparent group-hover:bg-dlg-hint-acc/92`;

@Component({
  selector: 'onboarding-coach-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, HlmButton],
  viewProviders: [provideIcons({ lucidePlay, lucideX })],
  host: {
    role: 'dialog',
    '[attr.aria-label]': 'title()',
    // Плиту задвинули внутрь экрана: `translate` — своё свойство, и анимация въезда на `transform` цела.
    '[style.translate.px]': 'shiftX()',
    class: `relative block ${COACH_PLATE}`,
  },
  styles: `
    /* Один набор перекрашиваемых свойств на оба места карточки: диск и крестик. Свойство, которое
       элемент не трогает, в переходе ничего не стоит. */
    :host {
      --coach-t-props: color, background-color, border, transform;
    }
  `,
  template: `
    <!-- Уголок смотрит на якорь: сторону и место по горизонтали задаёт оверлей. У края экрана карточка
         съезжает вбок, и уголок уезжает к её ближней грани — остриё остаётся над серединой якоря. -->
    <span aria-hidden="true" [class]="caretClass()" [style.translate.px]="-shiftX()"></span>

    @if (showCover()) {
      <button
        type="button"
        class="group relative block aspect-video w-full overflow-hidden bg-stage-black"
        [class]="coverRound()"
        [attr.aria-label]="door() ? 'Открыть видео-обзоры' : 'Смотреть урок'"
        (click)="mediaClicked.emit()"
      >
        <img [src]="cover()" alt="" class="absolute inset-0 size-full object-cover" />
        <!-- Затемнение кадра: под ним читаются и заголовок, и диск проигрывания. -->
        <span aria-hidden="true" class="absolute inset-0 bg-linear-to-b from-stage-black/20 to-stage-black/50"></span>

        <span aria-hidden="true" [class]="playDisc">
          <ng-icon name="lucidePlay" class="text-catalog-title [&>svg]:fill-current" />
        </span>

        <!-- Заголовок живёт на обложке: под ней остаётся только нижний ряд. -->
        <span
          class="absolute right-5.5 bottom-3.25 left-4.5 text-left text-base leading-5 font-semibold
                 text-on-media/88 [text-shadow:0_0.1em_0.3em_rgba(0,0,0,0.6)]
                 transition-[var(--coach-t-props)] duration-[var(--t-fast)] group-hover:-translate-y-px
                 group-hover:text-on-media"
        >
          {{ title() }}
        </span>

        <!-- Длительность — в левом верхнем углу обложки: правый верхний занят крестиком закрытия.
             У двери в библиотеку ролика нет — и чипа тоже. -->
        @if (clip() && duration()) {
          <span class="absolute top-2 left-2 rounded-full bg-[var(--scrim-ctrl-2)] px-2 py-0.5 text-fine text-on-media">
            {{ duration() }}
          </span>
        }
      </button>
    } @else {
      <!-- Обложки нет — заголовок встаёт на плиту. Правый отступ — воздух под крестиком закрытия:
           крестик сидит на самой плите, и хвост строки заехал бы под кнопку. -->
      <div class="px-4.5 pt-4" [class.pb-4]="!steps() && !actions().length">
        <h3 [class]="titleClass">{{ title() }}</h3>
      </div>
    }

    <!-- Карточка с кнопками зовёт к делу: текст записи объясняет, что это за дело. -->
    @if (actions().length) {
      @if (body()) {
        <p [class]="bodyClass">{{ body() }}</p>
      }
      <div class="flex items-center justify-end gap-1 px-4.5 pt-3 pb-3">
        @for (item of actions(); track $index) {
          @if (item.primary) {
            <button hlmBtn size="sm" type="button" class="px-3.5 text-sm font-medium" (click)="action.emit($index)">
              {{ item.label }}
            </button>
          } @else {
            <button
              hlmBtn
              variant="coach"
              size="sm"
              type="button"
              class="px-3.5 text-sm font-medium"
              (click)="action.emit($index)"
            >
              {{ item.label }}
            </button>
          }
        }
      </div>
    }

    <!-- Ряд внизу — только у очереди экрана: он несёт шаги. Одна карточка или спрошенная через «?»
         кончается обложкой: считать нечего, а закрывает её крестик. -->
    @if (steps()) {
      <div [class]="footerClass">
        <span class="text-sm text-c3">{{ position().index }} из {{ position().total }}</span>

        <span class="flex items-center gap-1">
          <button
            hlmBtn
            variant="coach"
            size="sm"
            type="button"
            class="px-3.5 text-sm font-medium"
            [disabled]="!canPrev()"
            (click)="prev.emit()"
          >
            Назад
          </button>
          <button hlmBtn size="sm" type="button" class="px-3.5 text-sm font-medium" [disabled]="!canNext()" (click)="next.emit()">
            Дальше
          </button>
        </span>
      </div>
    }

    <button
      hlmBtn
      variant="on-media-icon"
      size="icon-sm"
      type="button"
      class="absolute top-2 right-2 transition-[var(--coach-t-props)] duration-[var(--t-fast)]
             hover:scale-110 hover:bg-[var(--wash-on-dark)] hover:text-on-media"
      aria-label="Закрыть подсказку"
      (click)="closed.emit()"
    >
      <ng-icon name="lucideX" class="text-base" />
    </button>
  `,
})
export class CoachCardComponent {
  private readonly media = inject(OnboardingMediaService);

  readonly title = input.required<string>();
  /** Текст записи: карточка рисует его только с кнопками, иначе он живёт подзаголовком в библиотеке уроков. */
  readonly body = input('');
  /** Кнопки записи по порядку каталога; нажатие отдаёт номер кнопки в `action`. */
  readonly actions = input<readonly CoachAction[]>([]);
  readonly video = input.required<OnboardingVideo>();
  readonly caretSide = input.required<HintCaret>();
  /** Где уголок стоит по горизонтали: по центру карточки либо у той грани, которой она прижата к якорю. */
  readonly caretAlign = input<CoachCaretAlign>('center');
  /**
   * На сколько пикселей карточку задвинули внутрь экрана с её места у якоря: на узком экране ни одно
   * из мест не встаёт целиком, и оверлей двигает плиту сам. Уголок уходит на столько же обратно и
   * остаётся над серединой якоря.
   */
  readonly shiftX = input(0);
  /** Id записи: по нему берутся ролик и длительность. Пусто — ролика нет, и карточка без обложки. */
  readonly lessonId = input('');
  /** Место в очереди экрана, с единицы: `total` больше одного — внизу ряд шагов, иначе ряда нет вовсе. */
  readonly position = input<{ index: number; total: number }>({ index: 0, total: 0 });
  readonly canPrev = input(false);
  readonly canNext = input(false);
  /**
   * Карточка двери в библиотеку: своего ролика у неё нет, а обложка есть — нажатие открывает саму
   * библиотеку. Чипа длительности на такой обложке нет: считать нечего (R52).
   */
  readonly door = input(false);

  readonly closed = output<void>();
  readonly mediaClicked = output<void>();
  readonly prev = output<void>();
  readonly next = output<void>();
  readonly action = output<number>();

  /** Медиа-блок живёт, только пока ролик снят: выключатель каталога плюс сам файл в манифесте. */
  protected readonly clip = computed(() => (this.video().enabled ? this.media.media(this.lessonId()) : null));
  protected readonly duration = computed(() => this.media.duration(this.lessonId()));
  /** Обложка стоит и у снятого ролика, и у двери в библиотеку: картинка одна, разное только нажатие. */
  protected readonly showCover = computed(() => !!this.clip() || this.door());
  /** Шаги внизу — только у очереди из нескольких карточек. */
  protected readonly steps = computed(() => this.position().total > 1);
  /** Без ряда внизу карточка кончается обложкой — и нижние углы скругляет уже сама обложка. */
  protected readonly coverRound = computed(() => (this.steps() ? 'rounded-t-[var(--r-m)]' : 'rounded-[var(--r-m)]'));
  protected readonly cover = computed(() => this.clip()?.cover ?? COACH_COVER);
  protected readonly playDisc = PLAY_DISC;
  protected readonly caretClass = computed(() => coachCaret(this.caretSide(), this.caretAlign()));
  protected readonly titleClass = COACH_TITLE;
  protected readonly bodyClass = COACH_BODY;
  protected readonly footerClass = COACH_FOOTER;
}
